import type { ChangeEvent, FC, FormEvent, ReactNode } from 'react'

import Image from 'next/image'
import { useEffect, useRef, useState } from 'react'
import { ArrowRight, Check, LoaderCircle } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { cn } from '@/lib/utils'
import { join } from '@/constants/content'
import { siteConfig } from '@/constants/site'

type Field = 'emri' | 'email' | 'telefoni' | 'mesazhi'
type Values = Record<Field, string>
type Errors = Partial<Record<Field, string>>
type Status = 'idle' | 'submitting' | 'success' | 'error'

type InputField = {
    name: Exclude<Field, 'mesazhi'>
    label: string
    type: 'text' | 'email' | 'tel'
    autoComplete: string
}

const EMPTY: Values = { emri: '', email: '', telefoni: '', mesazhi: '' }
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
const PHONE_PATTERN = /^[+()\d\s./-]{6,30}$/

const INPUTS: Array<InputField> = [
    { name: 'emri', label: "Emri dhe mbiemri", type: 'text', autoComplete: 'name' },
    { name: 'email', label: "Email", type: 'email', autoComplete: 'email' },
    { name: 'telefoni', label: "Numri i telefonit", type: 'tel', autoComplete: 'tel' }
]

const validate = (values: Values): Errors => {
    const errors: Errors = {}
    const phone = values.telefoni.trim()

    if (values.emri.trim().length < 2) errors.emri = "Shkruaj emrin dhe mbiemrin."
    if (!EMAIL_PATTERN.test(values.email.trim())) errors.email = "Shkruaj një adresë emaili të vlefshme."
    if (!PHONE_PATTERN.test(phone) || phone.replace(/\D/g, '').length < 6) errors.telefoni = "Shkruaj një numër telefoni të vlefshëm."
    if (values.mesazhi.trim().length < 10) errors.mesazhi = "Na shkruaj të paktën disa fjalë për veten."

    return errors
}

const inputClass = 'mt-2 w-full border-0 border-b border-tone-line bg-transparent py-3 text-xl text-tone-fg outline-none transition-[border-color,box-shadow] duration-300 placeholder:text-tone-fg/35 focus:border-tone-accent focus:shadow-[0_1px_0_0_var(--tone-accent)] aria-[invalid=true]:border-jap-gold md:text-2xl'

// Join form: saved to the `aplikimet` collection and emailed to the team by /api/bashkohu.
export const Join: FC = (): ReactNode => {
    const [values, setValues] = useState<Values>(EMPTY)
    const [gifts, setGifts] = useState<Array<string>>([])
    const [errors, setErrors] = useState<Errors>({})
    const [touched, setTouched] = useState(false)
    const [status, setStatus] = useState<Status>('idle')
    const [message, setMessage] = useState('')
    const [honeypot, setHoneypot] = useState('')
    const successRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (status === 'success') successRef.current?.focus()
    }, [status])

    const update = (field: Field) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const next = { ...values, [field]: event.target.value }

        setValues(next)

        if (touched) setErrors(validate(next))
    }

    const toggleGift = (gift: string) => {
        setGifts((current) => (current.includes(gift) ? current.filter((item) => item !== gift) : [...current, gift]))
    }

    const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        if (status === 'submitting') return

        const found = validate(values)

        setTouched(true)
        setErrors(found)

        const firstInvalid = Object.keys(found)[0]

        if (firstInvalid) {
            document.getElementById(`bashkohu-${firstInvalid}`)?.focus()
            return
        }

        setStatus('submitting')
        setMessage('')

        try {
            const response = await fetch('/api/bashkohu', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...values, dhurata: gifts, website: honeypot })
            })

            const data = await response.json().catch(() => null) as { ok?: boolean, message?: string, errors?: Errors } | null

            if (response.ok && data?.ok) {
                setStatus('success')
                return
            }

            if (data?.errors) setErrors(data.errors)

            setStatus('error')
            setMessage(data?.message ?? "Diçka nuk shkoi mirë. Provo përsëri pas pak.")
        }
        catch {
            setStatus('error')
            setMessage("Nuk arritëm të lidhemi. Kontrollo internetin dhe provo përsëri.")
        }
    }

    const reset = () => {
        setValues(EMPTY)
        setGifts([])
        setErrors({})
        setTouched(false)
        setStatus('idle')
        setMessage('')
    }

    return (
        <section
            id='bashkohu'
            data-tone='purple'
            aria-labelledby='bashkohu-titulli'
            className='relative overflow-hidden px-5 py-28 md:px-8 md:py-40 lg:px-12'
        >
            <p className='flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.18em] text-tone-muted'>
                <span className='font-heading tabular-nums text-tone-accent'>07</span>
                <span aria-hidden='true' className='h-px w-8 bg-tone-line' />
                {join.label}
            </p>

            <h2
                id='bashkohu-titulli'
                className='mt-10 whitespace-nowrap font-heading text-[15.5vw] font-black leading-[0.82] tracking-[-0.06em] text-tone-accent md:text-[15vw] md:[font-stretch:110%]'
            >
                <span className='sr-only'>{join.title}</span>

                <span aria-hidden='true'>
                    {Array.from(join.title).map((char, index) => (
                        <span key={`${char}-${index}`} className='inline-block overflow-hidden py-[0.06em] align-top'>
                            <motion.span
                                initial={{ y: '105%' }}
                                whileInView={{ y: '0%' }}
                                viewport={{ once: true, amount: 0.5 }}
                                transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1], delay: index * 0.045 }}
                                className='inline-block'
                            >
                                {char}
                            </motion.span>
                        </span>
                    ))}
                </span>
            </h2>

            <div className='mt-16 grid grid-cols-12 gap-x-5 gap-y-16 md:mt-24'>
                <div className='col-span-12 flex flex-col gap-10 lg:col-span-5'>
                    <p className='max-w-lg text-xl leading-relaxed md:text-2xl'>
                        {join.text}
                    </p>

                    <figure className='w-full max-w-sm'>
                        <div className='relative aspect-[4/5] overflow-hidden bg-jap-night'>
                            <Image
                                src='/images/bashkohu-bashke.jpg'
                                alt={join.imageAlt}
                                fill
                                sizes='(min-width: 1024px) 24rem, 80vw'
                                className='duotone-lilac object-cover'
                            />
                        </div>

                        <figcaption className='mt-3 font-serif text-xl italic text-tone-muted'>
                            {join.imageCaption}
                        </figcaption>
                    </figure>

                    <p className='text-sm text-tone-muted'>
                        Pyetje?{' '}
                        <a href={`mailto:${siteConfig.email}`} className='font-semibold text-tone-fg underline underline-offset-4 hover:text-tone-accent'>
                            {siteConfig.email}
                        </a>
                    </p>
                </div>

                <div className='col-span-12 lg:col-span-6 lg:col-start-7'>
                    <AnimatePresence mode='wait' initial={false}>
                        {status === 'success' ? (
                            <motion.div
                                key='success'
                                ref={successRef}
                                tabIndex={-1}
                                role='status'
                                initial={{ opacity: 0, y: 24 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -12 }}
                                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                                className='flex flex-col items-start gap-6 border-t border-tone-line pt-10 outline-none'
                            >
                                <span aria-hidden='true' className='grid size-14 place-items-center bg-tone-accent text-tone-accent-fg'>
                                    <Check className='size-6' />
                                </span>

                                <p className='font-serif text-5xl italic md:text-6xl'>
                                    {join.successTitle}
                                </p>

                                <p className='max-w-md text-lg leading-relaxed text-tone-fg/85'>
                                    {join.successText}
                                </p>

                                <button
                                    type='button'
                                    onClick={reset}
                                    className='border-b border-current pb-1 text-sm font-semibold transition-colors hover:text-tone-accent'
                                >
                                    {join.again}
                                </button>
                            </motion.div>
                        ) : (
                            <motion.form
                                key='form'
                                noValidate
                                onSubmit={onSubmit}
                                aria-describedby='bashkohu-privatesia'
                                initial={{ opacity: 0, y: 24 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -12 }}
                                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                                className='relative flex flex-col gap-9'
                            >
                                {INPUTS.map((field) => {
                                    const id = `bashkohu-${field.name}`
                                    const error = errors[field.name]

                                    return (
                                        <div key={field.name}>
                                            <label htmlFor={id} className='block text-xs font-semibold uppercase tracking-[0.18em] text-tone-muted'>
                                                {field.label}
                                                <span aria-hidden='true' className='ml-1 text-tone-accent'>*</span>
                                            </label>

                                            <input
                                                id={id}
                                                name={field.name}
                                                type={field.type}
                                                autoComplete={field.autoComplete}
                                                required
                                                value={values[field.name]}
                                                onChange={update(field.name)}
                                                aria-invalid={Boolean(error)}
                                                aria-describedby={error ? `${id}-gabim` : undefined}
                                                className={inputClass}
                                            />

                                            {error && (
                                                <p id={`${id}-gabim`} className='mt-2 text-sm font-medium text-jap-gold'>
                                                    {error}
                                                </p>
                                            )}
                                        </div>
                                    )
                                })}

                                <fieldset>
                                    <legend className='text-xs font-semibold uppercase tracking-[0.18em] text-tone-muted'>
                                        Çfarë mund të japësh?
                                        <span className='ml-2 normal-case tracking-normal'>(opsionale)</span>
                                    </legend>

                                    <div className='mt-4 flex flex-wrap gap-2'>
                                        {join.gifts.map((gift) => {
                                            const checked = gifts.includes(gift)

                                            return (
                                                <label
                                                    key={gift}
                                                    className={cn(
                                                        'inline-flex cursor-pointer select-none items-center gap-2.5 border px-4 py-2.5 text-sm font-semibold transition-colors duration-300 has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-tone-accent',
                                                        checked ? 'border-tone-accent bg-tone-accent text-tone-accent-fg' : 'border-tone-line hover:border-tone-fg'
                                                    )}
                                                >
                                                    <input
                                                        type='checkbox'
                                                        name='dhurata'
                                                        value={gift}
                                                        checked={checked}
                                                        onChange={() => toggleGift(gift)}
                                                        className='sr-only'
                                                    />

                                                    <span
                                                        aria-hidden='true'
                                                        className={cn('grid size-4 place-items-center border', checked ? 'border-tone-accent-fg' : 'border-current')}
                                                    >
                                                        {checked && <Check className='size-3' />}
                                                    </span>

                                                    {gift}
                                                </label>
                                            )
                                        })}
                                    </div>
                                </fieldset>

                                <div>
                                    <label htmlFor='bashkohu-mesazhi' className='block text-xs font-semibold uppercase tracking-[0.18em] text-tone-muted'>
                                        Mesazhi
                                        <span aria-hidden='true' className='ml-1 text-tone-accent'>*</span>
                                    </label>

                                    <textarea
                                        id='bashkohu-mesazhi'
                                        name='mesazhi'
                                        required
                                        rows={4}
                                        value={values.mesazhi}
                                        onChange={update('mesazhi')}
                                        placeholder="Na trego pak për veten dhe si dëshiron të kontribuosh."
                                        aria-invalid={Boolean(errors.mesazhi)}
                                        aria-describedby={errors.mesazhi ? 'bashkohu-mesazhi-gabim' : undefined}
                                        className={cn(inputClass, 'resize-none text-lg md:text-xl')}
                                    />

                                    {errors.mesazhi && (
                                        <p id='bashkohu-mesazhi-gabim' className='mt-2 text-sm font-medium text-jap-gold'>
                                            {errors.mesazhi}
                                        </p>
                                    )}
                                </div>

                                <div aria-hidden='true' className='absolute -left-[9999px] top-0 h-px w-px overflow-hidden'>
                                    <label htmlFor='bashkohu-website'>Lëre këtë fushë bosh</label>
                                    <input
                                        id='bashkohu-website'
                                        name='website'
                                        type='text'
                                        tabIndex={-1}
                                        autoComplete='off'
                                        value={honeypot}
                                        onChange={(event) => setHoneypot(event.target.value)}
                                    />
                                </div>

                                {status === 'error' && message && (
                                    <p role='alert' className='border-l-2 border-jap-gold pl-4 text-base font-medium text-jap-gold'>
                                        {message}
                                    </p>
                                )}

                                <div className='flex flex-col gap-4'>
                                    <button
                                        type='submit'
                                        disabled={status === 'submitting'}
                                        className='group relative flex w-full items-center justify-between overflow-hidden bg-tone-accent px-6 py-5 text-left font-heading text-xl font-bold tracking-[-0.01em] text-tone-accent-fg disabled:cursor-wait md:text-2xl'
                                    >
                                        <span
                                            aria-hidden='true'
                                            className='absolute inset-0 origin-left scale-x-0 bg-jap-paper transition-transform duration-700 ease-out-expo group-hover:scale-x-100'
                                        />

                                        <span className='relative'>
                                            {status === 'submitting' ? join.sending : join.submit}
                                        </span>

                                        {status === 'submitting'
                                            ? <LoaderCircle aria-hidden='true' className='relative size-6 animate-spin' />
                                            : <ArrowRight aria-hidden='true' className='relative size-6 transition-transform duration-500 ease-out-expo group-hover:translate-x-1' />}
                                    </button>

                                    <p id='bashkohu-privatesia' className='text-sm text-tone-muted'>
                                        {join.privacy}
                                    </p>
                                </div>
                            </motion.form>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </section>
    )
}
